import { Injectable } from '@angular/core';
import { OfflineStorageService } from '@shared/services/offline/offline-storage.service';
import {
  OfflinePendingMutation,
  OfflinePendingMutationType,
} from '@shared/models/offline/offline.model';
import { AddFormReponseRequest, UpdateFormResponseRequest } from '@shared/models/form/form.model';
import { generateClientMutationId, normalizeFormCode } from '@shared/utilities/offline-cache-key.util';

@Injectable({ providedIn: 'root' })
export class OfflineQueueService {
  constructor(private readonly storage: OfflineStorageService) {}

  async enqueueAddFormResponse(
    params: AddFormReponseRequest,
    serviceRequestId: number,
    formCode: string,
    formId: number,
    clientMutationId?: string,
  ): Promise<OfflinePendingMutation> {
    return this.enqueue(
      'addFormResponse',
      params.clientId,
      serviceRequestId,
      formCode,
      formId,
      params,
      clientMutationId,
    );
  }

  async enqueueUpdateFormResponse(
    params: UpdateFormResponseRequest,
    serviceRequestId: number,
    formCode: string,
    formId: number,
    clientMutationId?: string,
  ): Promise<OfflinePendingMutation> {
    return this.enqueue(
      'updateFormResponse',
      params.clientId,
      serviceRequestId,
      formCode,
      formId,
      params,
      clientMutationId,
    );
  }

  async listPending(): Promise<OfflinePendingMutation[]> {
    return this.storage.listPendingMutations();
  }

  async remove(mutationId: string): Promise<void> {
    await this.storage.deletePendingMutation(mutationId);
  }

  async markFailed(mutationId: string, error: string): Promise<void> {
    const mutation = await this.storage.getPendingMutation(mutationId);
    if (!mutation) {
      return;
    }

    await this.storage.putPendingMutation({
      ...mutation,
      attempts: mutation.attempts + 1,
      lastError: error,
    });
  }

  private async enqueue(
    type: OfflinePendingMutationType,
    clientId: string,
    serviceRequestId: number,
    formCode: string,
    formId: number,
    payload: AddFormReponseRequest | UpdateFormResponseRequest,
    clientMutationId?: string,
  ): Promise<OfflinePendingMutation> {
    const mutationId = generateClientMutationId();
    const mutation: OfflinePendingMutation = {
      mutationId,
      clientMutationId: clientMutationId ?? generateClientMutationId(),
      type,
      clientId,
      serviceRequestId,
      formCode: normalizeFormCode(formCode),
      formId,
      payload,
      createdAt: new Date().toISOString(),
      attempts: 0,
    };

    await this.storage.putPendingMutation(mutation);
    return mutation;
  }
}
